/**
 * The `extraction_jobs` collection, as the admin reads it.
 *
 * The extractor writes these docs with the Admin SDK (memo §12, locked answer
 * 3) and the admin only ever reads them, live, with `onSnapshot` — so a job
 * that starts on the laptop appears in the history table without the admin
 * polling anything. The status pill moving from queued → running → completed is
 * the runner updating the same doc.
 *
 * Types and the filter/sort helpers only: the subscription itself lives in
 * `extractorService.ts` because importing `./firebase` here would drag the
 * whole SDK (and a browser-only HEIC decoder) into a unit test. The helpers are
 * the part with rules in them anyway — what "recent" means, which timestamp a
 * row is ordered by when it never started, whether a zero result count is a
 * count or a gap.
 */

import type { Timestamp } from 'firebase/firestore';

export const EXTRACTION_JOBS_COLLECTION = 'extraction_jobs';

/** Lifecycle states, in the order the runner moves through them. */
export type ExtractionJobStatus = 'queued' | 'running' | 'completed' | 'failed' | 'cancelled';

export const JOB_STATUSES: readonly ExtractionJobStatus[] = [
  'queued',
  'running',
  'completed',
  'failed',
  'cancelled',
];

/** Terminal states — nothing further will be written to these docs. */
export const TERMINAL_STATUSES: readonly ExtractionJobStatus[] = [
  'completed',
  'failed',
  'cancelled',
];

export interface ExtractionJobError {
  code?: string;
  message?: string;
  url?: string;
  at?: Timestamp;
}

export interface ExtractionJobStats {
  discovered?: number;
  fetched?: number;
  parsed?: number;
  failed?: number;
  skipped?: number;
}

/**
 * The job doc as it arrives from Firestore.
 *
 * A local type rather than shared-types' `ExtractionJob`: that one declares
 * `createdAt: Date` because it describes the runner's in-memory value, and what
 * a Firestore read hands back is a `Timestamp`. Every field is optional except
 * the id because this doc is written by another service — a doc from an older
 * build of the extractor is a normal thing to encounter in the history table
 * and must render, not crash.
 */
export interface ExtractorJobDoc {
  jobId: string;
  extractorId?: string;
  brandId?: string;
  brandSlug?: string;
  status?: ExtractionJobStatus;
  options?: {
    mode?: string;
    limit?: number;
    offset?: number;
    concurrency?: number;
    politenessDelayMs?: number;
    familySlugs?: string[];
  };
  progressPercent?: number;
  progressLabel?: string;
  stats?: ExtractionJobStats;
  errors?: ExtractionJobError[];
  /** Absolute path on the machine that ran the job — not a URL. */
  resultPath?: string;
  cancelRequested?: boolean;
  createdAt?: Timestamp;
  createdBy?: string;
  updatedAt?: Timestamp;
  startedAt?: Timestamp;
  completedAt?: Timestamp;
  /** Stamped by `runExtractorJob` so admin-triggered runs are identifiable. */
  triggeredFrom?: string;
  /**
   * Who ended the job, when it did not end itself. `'admin-force'` is the only
   * value written today, by `forceCloseExtractorJob` — it is what tells a
   * `cancelled` row apart from one the extractor cancelled cleanly, which
   * otherwise look identical.
   */
  endedBy?: string;
  endedByUid?: string;
  endReason?: string;
  /**
   * Written by the generic image downloader (`extractorId:
   * 'generic-image-downloader'`) when it completes. Read through
   * `readImageDownloadReport`, never directly — a doc from an extractor build
   * without the job kind simply has none of these.
   */
  imageDownload?: unknown;
  imageFailures?: unknown;
  imageFailureCount?: number;
}

export interface JobFilters {
  brandId?: string;
  status?: ExtractionJobStatus | 'all' | 'active';
  /** Matched against jobId, brandId and extractorId. */
  search?: string;
  /**
   * Hide rows older than this many days. Composes with `status` rather than
   * duplicating it: "hide failed runs older than a week" is this plus
   * `status: 'failed'`, which is two controls the screen already needs instead
   * of one per status.
   */
  maxAgeDays?: number;
}

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * `active` is its own filter value because it is the one an operator actually
 * wants: "is anything running right now", which spans queued and running and is
 * two clicks otherwise.
 */
export function filterJobs(
  jobs: readonly ExtractorJobDoc[],
  filters: JobFilters,
  nowMs: number = Date.now(),
): ExtractorJobDoc[] {
  const search = filters.search?.trim().toLowerCase() ?? '';
  const cutoff =
    typeof filters.maxAgeDays === 'number' && filters.maxAgeDays > 0
      ? nowMs - filters.maxAgeDays * DAY_MS
      : null;

  return jobs.filter((job) => {
    if (filters.brandId && job.brandId !== filters.brandId) return false;

    if (cutoff !== null) {
      /*
       * Same timestamp `sortJobs` orders on, so the age filter cuts the list
       * where the list is ordered. A job carrying neither is kept: an undated
       * row is a malformed doc, and hiding it would remove the only place it is
       * visible enough to be dealt with.
       */
      const at = Math.max(millis(job.createdAt), millis(job.startedAt));
      if (at !== 0 && at < cutoff) return false;
    }

    if (filters.status && filters.status !== 'all') {
      if (filters.status === 'active') {
        if (job.status !== 'queued' && job.status !== 'running') return false;
      } else if (job.status !== filters.status) {
        return false;
      }
    }

    if (search !== '') {
      const haystack = [job.jobId, job.brandId, job.extractorId, job.options?.mode]
        .filter((v): v is string => typeof v === 'string')
        .join(' ')
        .toLowerCase();
      if (!haystack.includes(search)) return false;
    }

    return true;
  });
}

function millis(value: Timestamp | undefined): number {
  return value ? value.toMillis() : 0;
}

/**
 * Newest first, on the timestamp a row is actually about.
 *
 * `startedAt` over `createdAt`, because a queued job that has not started is
 * the newest thing on the screen and should sort as such — and because the two
 * are seconds apart for everything else, so this only changes the order in the
 * case it exists for. Ties break on jobId so the list does not shuffle between
 * snapshots when two jobs share a second.
 */
export function sortJobs(jobs: readonly ExtractorJobDoc[]): ExtractorJobDoc[] {
  return [...jobs].sort((a, b) => {
    const delta =
      Math.max(millis(b.createdAt), millis(b.startedAt)) -
      Math.max(millis(a.createdAt), millis(a.startedAt));
    if (delta !== 0) return delta;
    return a.jobId < b.jobId ? 1 : a.jobId > b.jobId ? -1 : 0;
  });
}

/** How long the job ran, in ms, or null when it has not finished. */
export function jobDurationMs(job: ExtractorJobDoc): number | null {
  const start = millis(job.startedAt) || millis(job.createdAt);
  const end = millis(job.completedAt);
  if (!start || !end || end < start) return null;
  return end - start;
}

/** `1m 04s` / `12s` / `1h 03m` — a duration read at a glance in a table cell. */
export function formatDuration(ms: number | null): string {
  if (ms === null) return '—';
  const totalSeconds = Math.round(ms / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/**
 * References emitted, when the job got far enough to say.
 *
 * `parsed` rather than `fetched`: a page that was fetched and failed to parse
 * produced nothing, and the number this column is answering is "how many
 * watches will land in the import".
 */
export function jobResultCount(job: ExtractorJobDoc): number | null {
  const parsed = job.stats?.parsed;
  return typeof parsed === 'number' ? parsed : null;
}

/** First error message, trimmed to a table cell. */
export function jobErrorSnippet(job: ExtractorJobDoc, maxLength = 120): string | null {
  const first = job.errors?.find((e) => e?.message || e?.code);
  if (!first) return null;
  const text = first.message ?? first.code ?? '';
  const prefix = first.code && first.message ? `${first.code}: ` : '';
  const full = `${prefix}${text}`;
  return full.length > maxLength ? `${full.slice(0, maxLength - 1)}…` : full;
}

/** Tailwind classes per status — one place, so the pill and the badge agree. */
export function statusClasses(status: ExtractionJobStatus | undefined): string {
  switch (status) {
    case 'running':
      return 'bg-blue-100 text-blue-800';
    case 'queued':
      return 'bg-gray-100 text-gray-700';
    case 'completed':
      return 'bg-green-100 text-green-800';
    case 'failed':
      return 'bg-red-100 text-red-800';
    case 'cancelled':
      return 'bg-amber-100 text-amber-800';
    default:
      return 'bg-gray-100 text-gray-500';
  }
}

/**
 * Nothing further will be written to this job.
 *
 * A job with no status yet is NOT terminal: the doc is written by the extractor
 * and a run that has just been accepted can be read a moment before its status
 * field exists, which is a job about to start rather than one that has ended.
 */
export function isJobTerminal(job: ExtractorJobDoc | null | undefined): boolean {
  return job?.status !== undefined && TERMINAL_STATUSES.includes(job.status);
}

/**
 * A row that can be removed from the history.
 *
 * Only a finished one. The runner writes progress into a live job's doc as it
 * goes, so deleting one mid-crawl either resurrects it half-written on the next
 * update or fails the run — the operator cancels first and deletes the row that
 * leaves. The server refuses these too; this is what keeps the UI from offering
 * a button whose only outcome is a refusal.
 *
 * A job with no status is deletable: that is a malformed doc, which is exactly
 * the kind of row this feature exists to clear out.
 */
export function isJobDeletable(job: ExtractorJobDoc | null | undefined): boolean {
  if (!job) return false;
  return job.status !== 'queued' && job.status !== 'running';
}

/** Cancel is only offered where it can do something (extractor returns 409 otherwise). */
export function isCancellable(job: ExtractorJobDoc): boolean {
  return (
    (job.status === 'queued' || job.status === 'running') && job.cancelRequested !== true
  );
}

// ---------------------------------------------------------------------------
// Stuck jobs
// ---------------------------------------------------------------------------

/**
 * How long a cancellation may sit unacknowledged before the row is stuck.
 *
 * The runner re-reads `cancelRequested` every 5 seconds
 * (`CANCEL_POLL_INTERVAL_MS` in the extractor's `runner.ts`) and exits on the
 * next poll, so five minutes is sixty missed polls. Anything that has not
 * acknowledged by then is not being slow, it is not running.
 */
export const STUCK_CANCELLING_MS = 5 * 60 * 1000;

/**
 * How long a running job may report nothing before the row is stuck.
 *
 * Much longer than the two-second progress flush, because `updatedAt` is a
 * progress signal rather than a heartbeat: the extractor's reporter no-ops when
 * nothing is pending and modules report once per watch, so the field genuinely
 * stops moving during browser launch, the discovery crawl before the first
 * watch is emitted, and any configured `politenessDelayMs`. Thirty minutes
 * clears the longest of those with room to spare, which matters more here than
 * reaping promptly — a threshold that fires on a live crawl offers an operator
 * a button that kills real work.
 */
export const STUCK_RUNNING_MS = 30 * 60 * 1000;

/**
 * How long a job may sit queued before the row is stuck.
 *
 * The extractor starts an accepted job immediately, so a queued row is only
 * ever a moment old in the normal case. Shares the running threshold rather
 * than picking a tighter one of its own: the number that matters is "longer
 * than anything healthy", and there is nothing to gain from reaping a dead
 * queued job twenty minutes sooner.
 */
export const STUCK_QUEUED_MS = STUCK_RUNNING_MS;

/** Why a row qualifies — drives the wording the confirmation shows. */
export type StuckReason = 'cancelling' | 'running' | 'queued';

/**
 * The last moment this job is known to have been alive.
 *
 * `updatedAt` is the real answer: the extractor stamps it on every progress
 * flush and on the cancel request itself. The fallbacks are for docs written by
 * an older build of the extractor, which the history table has to cope with —
 * they render, so they must be reapable too.
 */
function lastSignMs(job: ExtractorJobDoc): number {
  return millis(job.updatedAt) || millis(job.startedAt) || millis(job.createdAt);
}

/**
 * Why this row cannot get out of its own state, or null when it is fine.
 *
 * The cancelling branch is first because it is the trap that has no other way
 * out: `isCancellable` hides the Cancel button the moment `cancelRequested` is
 * set, so a runner that died between the flag landing and the doc closing
 * leaves a row with no button at all. It covers `queued` as well as `running` —
 * a queued job has no runner polling the flag, so it is the same dead end
 * reached sooner.
 *
 * A job with no usable timestamp is stuck rather than healthy. That is a
 * malformed doc, and `isJobDeletable` already takes the same view of one: the
 * rows this feature exists to clear are exactly the rows that are wrong.
 */
export function stuckJobReason(
  job: ExtractorJobDoc | null | undefined,
  nowMs: number = Date.now(),
): StuckReason | null {
  if (!job) return null;
  if (job.status !== 'queued' && job.status !== 'running') return null;

  const last = lastSignMs(job);
  const silentFor = last === 0 ? Infinity : nowMs - last;

  if (job.cancelRequested === true) {
    return silentFor > STUCK_CANCELLING_MS ? 'cancelling' : null;
  }
  if (job.status === 'running') {
    return silentFor > STUCK_RUNNING_MS ? 'running' : null;
  }
  return silentFor > STUCK_QUEUED_MS ? 'queued' : null;
}

/** Force-close is only offered on a row that cannot leave its state on its own. */
export function isJobStuck(
  job: ExtractorJobDoc | null | undefined,
  nowMs: number = Date.now(),
): boolean {
  return stuckJobReason(job, nowMs) !== null;
}

/** What the confirmation tells the operator it is about to overrule. */
export function stuckReasonLabel(reason: StuckReason): string {
  switch (reason) {
    case 'cancelling':
      return 'cancelling, but the extractor never acknowledged it';
    case 'running':
      return 'running, but has reported no progress';
    case 'queued':
      return 'queued, but never started';
  }
}

/**
 * The most recent run per extractor module, keyed by `extractorId`.
 *
 * WHAT THIS IS NOT: a field the extractor service publishes. The catalogue
 * descriptor carries no run history at all, so "last run" is DERIVED here from
 * the admin's own `extraction_jobs` collection — which means it answers a
 * narrower question than its name suggests, and the column that renders it says
 * so:
 *
 *  - Only runs STARTED FROM THIS ADMIN write a job document. A crawl kicked off
 *    from the extractor's CLI leaves no trace here, so a module that ran an hour
 *    ago can legitimately show nothing.
 *  - The caller reads a bounded, newest-first page of jobs. A module whose last
 *    run falls outside that page is indistinguishable from one that never ran.
 *
 * Uses `max(startedAt, createdAt)` — the same instant `sortJobs` orders on — so
 * a queued job that has not started still counts as the module's latest
 * activity, rather than the screen claiming an older completed run is newer.
 */
export function lastRunByExtractorId(
  jobs: readonly ExtractorJobDoc[],
): Map<string, number> {
  const out = new Map<string, number>();
  for (const job of jobs) {
    const id = job.extractorId?.trim();
    if (!id) continue;
    const at = Math.max(millis(job.createdAt), millis(job.startedAt));
    if (at <= 0) continue;
    const seen = out.get(id);
    if (seen === undefined || at > seen) out.set(id, at);
  }
  return out;
}

/**
 * A timestamp as "3h ago" for anything recent, and a plain date once it is old
 * enough that elapsed time stops being the useful reading.
 *
 * The crossover is a week: inside it an operator is asking "has this run since
 * the thing I changed?", for which a relative answer is immediate; beyond it
 * they are asking "when was this last touched at all?", for which "37d ago" is
 * arithmetic and a date is not.
 */
export function formatLastRun(at: number | undefined, now = Date.now()): string {
  if (at === undefined || at <= 0) return '—';
  const delta = now - at;
  if (delta < 0) return new Date(at).toLocaleDateString();
  const minutes = Math.floor(delta / 60_000);
  if (minutes < 1) return 'just now';
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(at).toLocaleDateString();
}
